import { z } from 'zod'

import { userApi } from '../users/paths'

/** Find Blockouts, Create Blockout Time and My Blockout Times. */
export const blockoutsPath = '/site-management/blockouts'
export const createBlockoutPath = `${blockoutsPath}/new`
export const myBlockoutsPath = '/site-management/my-blockout-times'
export const blockoutRoute = `${blockoutsPath}/:userId/:blockoutId`

/** A blockout time's details page, under its instructor. */
export function blockoutPath(userId: string, id: string): string {
  return `${blockoutsPath}/${encodeURIComponent(userId)}/${encodeURIComponent(id)}`
}

/** What Find Instructors and choosing an instructor on Create Blockout Time need: both search the site's users. */
export const readUsersPermission = 'site-management.user-profiles.read'

/** The API's site-wide search of blockout times. */
export const blockoutsApi = '/site-management/v1/blockouts'

/** The API's own blockout times, for My Blockout Times. */
export const myBlockoutsApi = '/site-management/v1/me/blockouts'

/** A user's blockout times: GET lists them, POST adds one. */
export function userBlockoutsApi(userId: string): string {
  return `${userApi(userId)}/blockouts`
}

/** One blockout time under a list's path: GET, PUT and DELETE. */
export function blockoutApi(listApi: string, id: string): string {
  return `${listApi}/${encodeURIComponent(id)}`
}

/** What each blockout action needs. */
export const readBlockoutsPermission = 'site-management.instructor-blockouts.read'
export const writeBlockoutsPermission = 'site-management.instructor-blockouts.write'
export const deleteBlockoutsPermission = 'site-management.instructor-blockouts.delete'

/** The most characters each field holds, as the API allows. */
export const blockoutNameMaxLength = 100
export const blockoutDescriptionMaxLength = 1000

const blockoutSchema = z.object({
  id: z.string(),
  userId: z.string(),
  userName: z.string().nullish(),
  name: z.string(),
  description: z.string().nullish(),
  startsOn: z.string(),
  endsOn: z.string(),
})

/** A time an instructor cannot teach. */
export type Blockout = z.infer<typeof blockoutSchema>

export function parseBlockout(body: unknown): Blockout {
  return blockoutSchema.parse(body)
}

/**
 * The instant a day and time of day name in an IANA time zone: the first of the two in an hour the
 * clocks repeat, and `null` for a time they skip.
 */
export function zonedInstant(day: string, time: string, timeZone: string): Date | null {
  const [year = 0, month = 1, date = 1] = day.split('-').map(Number)
  const [hour = 0, minute = 0] = time.split(':').map(Number)
  const asUtc = Date.UTC(year, month - 1, date, hour, minute)
  // Each offset the zone uses within a day either side: offsets reach 14 hours, so a clock change
  // near this time falls between them.
  const fullDay = 24 * 60 * 60 * 1000
  const offsets = new Set([asUtc - fullDay, asUtc, asUtc + fullDay].map((instant) => offsetAt(instant, timeZone)))
  const matches = [...offsets]
    .map((offset) => new Date(asUtc - offset))
    .filter((instant) => timeOfDay(instant, timeZone) === time && localDay(instant, timeZone) === day)
    .toSorted((first, second) => first.getTime() - second.getTime())
  return matches[0] ?? null
}

/** The time of day an instant shows in an IANA time zone, as `HH:mm`. */
export function timeOfDay(instant: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone, hourCycle: 'h23', hour: '2-digit', minute: '2-digit' }).formatToParts(instant)
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((entry) => entry.type === type)?.value ?? '00'
  return `${part('hour')}:${part('minute')}`
}

function localDay(instant: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(instant)
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((entry) => entry.type === type)?.value ?? ''
  return `${part('year')}-${part('month')}-${part('day')}`
}

// How far ahead of UTC a zone's clocks are at an instant, in milliseconds.
function offsetAt(instant: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(instant))
  const part = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((entry) => entry.type === type)?.value ?? 0)
  const local = Date.UTC(part('year'), part('month') - 1, part('day'), part('hour'), part('minute'), part('second'))
  return local - Math.floor(instant / 1000) * 1000
}
